/**
 * Alerts: an inbox, an archive, and a floor that sweeps itself (plan 037).
 *
 * The page is a triage queue, not a feed. Alerts arrive in the inbox; a
 * parent classifies one (which collapses it to a single line so the day's
 * shape stays honest) and archives it (which removes it). Archived alerts
 * are permanently deleted 30 days later by a server-side reaper.
 *
 * Nothing on this page asks "are you sure?" except emptying the whole
 * archive at once. Archiving is reversible, so a confirmation would be
 * noise; permanent deletion is only reachable from a tab you navigated to
 * on purpose, having already archived the row — that is the confirmation.
 *
 * Each row is fetched as opaque ciphertext and decrypted on the client
 * against the in-memory family key; results land in `decryptCache` so
 * re-renders are free.
 */

import { useEffect, useState } from 'preact/hooks';
import { Archive, ArchiveRestore, RotateCcw, Trash2 } from 'lucide-preact';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DescriptionCard, type Severity } from '../components/DescriptionCard';
import { cn } from '../lib/cn';
import {
  alerts,
  archive,
  archiveBulk,
  archivedTotal,
  dateFrom,
  dateTo,
  daysUntilPurge,
  decryptAlert,
  endpointFilter,
  inboxTotal,
  loading,
  page,
  pageCount,
  PAGE_SIZE,
  purge,
  refresh,
  remove,
  resetFilters,
  restore,
  restoreBulk,
  retentionDays,
  selection,
  setPage,
  setTab,
  showArchived,
  toggleSelectAll,
  toggleSelected,
  total,
  triage,
  type AlertRow,
  type TriageState,
} from '../data/alerts';
import {
  activityByEndpoint,
  endpoints,
  refresh as refreshEndpoints,
} from '../data/endpoints';
import { familyKeyInMemory } from '../state/session';

/**
 * Decrypt `row` once the family key is in memory. The key hydrates
 * asynchronously after a reload, so the effect keys on it — otherwise a
 * card mounted before the key lands stays in DECRYPTING forever.
 */
function useDecrypt(row: AlertRow): void {
  const fk = familyKeyInMemory.value;
  useEffect(() => {
    if (
      fk !== null &&
      row.decrypted === undefined &&
      row.decryptFailed !== true
    ) {
      void decryptAlert(row.id);
    }
  }, [fk, row.id, row.decrypted, row.decryptFailed]);
}

/** The row the undo toast refers to, or null when no toast is showing. */
interface Toast {
  id: string;
  label: string;
}

function endpointLabel(row: AlertRow): string {
  const match = endpoints.value.find((e) => e.id === row.endpoint_id);
  if (match !== undefined && match.label !== null && match.label.length > 0) {
    return match.label;
  }
  return row.endpoint_id.slice(0, 8);
}

function relativeTime(iso: string): string {
  const diffSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${String(diffSec)}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${String(diffMin)} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${String(diffHr)}h ago`;
  return new Date(iso).toLocaleString();
}

/** "Today" / "Yesterday" / "12 August" for a day-group heading. */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const midnight = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

/** Group the page's rows by calendar day, preserving server order. */
function groupByDay(rows: AlertRow[]): { label: string; rows: AlertRow[] }[] {
  const out: { label: string; rows: AlertRow[] }[] = [];
  for (const row of rows) {
    const label = dayLabel(row.received_at);
    const last = out[out.length - 1];
    if (last !== undefined && last.label === label) {
      last.rows.push(row);
    } else {
      out.push({ label, rows: [row] });
    }
  }
  return out;
}

/**
 * Alerts-page empty state. Instead of "everything is fine" (which reads as
 * "nothing is happening"), summarize what Backstop actually did.
 */
function EmptyStateSummary() {
  useEffect(() => {
    void refreshEndpoints();
  }, []);

  if (showArchived.value) {
    return (
      <div class="rounded-[4px] border border-line bg-surface-2 px-6 py-8 text-center text-surface-muted">
        Nothing archived. Alerts you archive land here and are deleted{' '}
        {String(retentionDays.value)} days later.
      </div>
    );
  }

  let totalScans = 0;
  let latestIso: string | null = null;
  for (const rows of Object.values(activityByEndpoint.value)) {
    totalScans += rows.length;
    for (const r of rows) {
      if (latestIso === null || r.at > latestIso) latestIso = r.at;
    }
  }
  const enrolled = endpoints.value.length;

  if (enrolled === 0) {
    return (
      <div class="rounded-[4px] border border-line bg-surface-2 px-6 py-8 text-center text-surface-muted">
        No devices enrolled yet. Add one from the Dashboard to start capturing.
      </div>
    );
  }

  return (
    <div class="rounded-[4px] border border-line bg-surface-2 px-6 py-8 space-y-2 text-center">
      <p class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted">
        Inbox clear
      </p>
      <p class="text-surface-fg">
        {totalScans === 0
          ? `Backstop is watching ${String(enrolled)} device${enrolled === 1 ? '' : 's'} but hasn’t captured yet.`
          : `Backstop has checked ${String(totalScans)} screenshot${totalScans === 1 ? '' : 's'} across ${String(enrolled)} device${enrolled === 1 ? '' : 's'}${
              latestIso === null
                ? ''
                : ` — most recent ${new Date(latestIso).toLocaleString()}`
            }. None matched your taxonomy.`}
      </p>
    </div>
  );
}

/** Triage buttons. Classifying does not remove the row — archiving does. */
function TriageRow({ row }: { row: AlertRow }) {
  const [busy, setBusy] = useState<TriageState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function set(state: TriageState): Promise<void> {
    setBusy(state);
    setErr(null);
    try {
      await triage(row.id, state);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="flex flex-wrap items-center gap-2 pt-1">
      {(
        [
          ['reviewed', 'Reviewed'],
          ['false_positive', 'False positive'],
          ['discussed', 'Discussed'],
        ] as const
      ).map(([state, label]) => (
        <button
          key={state}
          type="button"
          class="rounded border border-line px-2 py-1 text-[12px] hover:bg-surface-muted/10 disabled:opacity-50"
          disabled={busy !== null}
          onClick={() => void set(state)}
        >
          {busy === state ? 'Saving…' : label}
        </button>
      ))}
      {err !== null ? (
        <span class="text-[12px] text-red-600">{err}</span>
      ) : null}
    </div>
  );
}

/** A checkbox that only renders while selection mode is on. */
function SelectBox({ id, on }: { id: string; on: boolean }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selection.value.has(id)}
      aria-label="Select alert"
      onClick={() => {
        toggleSelected(id);
      }}
      class={cn(
        'mt-0.5 h-4 w-4 shrink-0 rounded-[3px] border',
        selection.value.has(id)
          ? 'border-brand-500 bg-brand-500'
          : 'border-line-strong bg-surface',
        !on && 'hidden',
      )}
    >
      {selection.value.has(id) ? (
        <svg viewBox="0 0 24 24" class="h-3 w-3 text-white" aria-hidden>
          <path
            d="M20 6 9 17l-5-5"
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      ) : null}
    </button>
  );
}

/** Full card — an untriaged inbox alert, the thing that needs attention. */
function AlertCard({
  row,
  selecting,
  onArchive,
}: {
  row: AlertRow;
  selecting: boolean;
  onArchive: (row: AlertRow) => void;
}) {
  useDecrypt(row);

  const severity: Severity =
    row.decryptFailed === true
      ? 'note'
      : row.decrypted === undefined
        ? 'decrypting'
        : row.severity_bucket === 1
          ? 'attention'
          : 'note';

  return (
    <div class="flex items-start gap-3">
      <SelectBox id={row.id} on={selecting} />
      <div class="min-w-0 flex-1">
        <DescriptionCard
          endpoint={endpointLabel(row)}
          time={relativeTime(row.received_at)}
          severity={severity}
          actions={
            <button
              type="button"
              aria-label="Archive alert"
              onClick={() => {
                onArchive(row);
              }}
              class="absolute top-3 right-3 flex items-center gap-1.5 rounded border border-line bg-surface px-2 py-1 text-[12px] text-surface-body hover:text-surface-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <Archive size={13} aria-hidden />
              Archive
            </button>
          }
        >
          {row.decrypted !== undefined ? (
            <div class="space-y-2">
              <div class="font-mono text-[11px] tracking-[0.14em] uppercase text-signal">
                {row.decrypted.category_label}
              </div>
              <p class="text-surface-fg">{row.decrypted.reason}</p>
              {row.decrypted.screen_summary.length > 0 && (
                <p class="text-[13px] text-surface-muted italic">
                  Screen: {row.decrypted.screen_summary}
                </p>
              )}
              <TriageRow row={row} />
            </div>
          ) : row.decryptFailed === true ? (
            'This alert could not be decrypted on this device. Try loading the recovery mnemonic under Settings.'
          ) : null}
        </DescriptionCard>
      </div>
    </div>
  );
}

/**
 * One-line row: a triaged inbox alert, or anything in the archive. This is
 * what makes the queue visibly drain as a parent works it.
 */
function CompactRow({
  row,
  selecting,
  onArchive,
}: {
  row: AlertRow;
  selecting: boolean;
  onArchive: (row: AlertRow) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const archived = row.archived_at !== null;
  const days = daysUntilPurge(row);

  useDecrypt(row);

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex items-center gap-3">
      <SelectBox id={row.id} on={selecting} />
      <div
        class={cn(
          'flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 rounded-[4px] border border-line bg-surface px-4 py-2',
          busy && 'opacity-60',
          err !== null && 'border-signal bg-signal-bg',
        )}
      >
        <span
          aria-hidden
          class="h-1.5 w-1.5 shrink-0 rounded-full bg-line-strong"
        />
        <span class="shrink-0 font-mono text-[12px] text-surface-muted">
          — {endpointLabel(row)}
        </span>
        <span class="min-w-0 flex-1 truncate text-[13.5px] text-surface-body">
          {err !== null
            ? `Couldn’t complete that — ${err}`
            : (row.decrypted?.reason ?? 'Decrypting…')}
        </span>

        {archived && days !== null ? (
          <span
            class={cn(
              'shrink-0 font-mono text-[11px]',
              days <= 3 ? 'text-signal' : 'text-surface-muted',
            )}
          >
            {days === 0 ? 'Deletes today' : `Deletes in ${String(days)} days`}
          </span>
        ) : (
          <span class="shrink-0 font-mono text-[11px] text-surface-muted">
            {row.triage_state.replace('_', ' ')}
          </span>
        )}

        {archived ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => restore(row.id))}
              class="flex shrink-0 items-center gap-1 rounded border border-line px-2 py-0.5 text-[12px] text-surface-body hover:text-surface-fg disabled:opacity-50"
            >
              <RotateCcw size={12} aria-hidden />
              Restore
            </button>
            <button
              type="button"
              aria-label="Delete permanently"
              disabled={busy}
              onClick={() => void run(() => remove(row.id))}
              class="shrink-0 rounded p-1 text-surface-muted hover:text-signal disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden />
            </button>
          </>
        ) : (
          <button
            type="button"
            aria-label="Archive alert"
            disabled={busy}
            onClick={() => {
              onArchive(row);
            }}
            class="shrink-0 rounded p-1 text-surface-muted hover:text-surface-fg disabled:opacity-50"
          >
            <Archive size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

/** Compact filter bar — chips, not a five-column grid. */
function FilterBar() {
  useEffect(() => {
    void refreshEndpoints();
  }, []);

  const selected = new Set(endpointFilter.value);
  const anyFilter =
    selected.size > 0 || dateFrom.value !== null || dateTo.value !== null;

  return (
    <div
      class="flex flex-wrap items-center gap-2"
      data-testid="alerts-filter-bar"
    >
      <button
        type="button"
        onClick={() => {
          endpointFilter.value = [];
          page.value = 0;
        }}
        class={cn(
          'h-7 rounded border px-2.5 text-[13px]',
          selected.size === 0
            ? 'border-brand-500 bg-brand-500 text-white'
            : 'border-line bg-surface text-surface-body',
        )}
      >
        All devices
      </button>
      {endpoints.value.map((ep) => (
        <button
          key={ep.id}
          type="button"
          onClick={() => {
            endpointFilter.value = selected.has(ep.id)
              ? endpointFilter.value.filter((x) => x !== ep.id)
              : [...endpointFilter.value, ep.id];
            page.value = 0;
          }}
          class={cn(
            'h-7 rounded border px-2.5 text-[13px]',
            selected.has(ep.id)
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-line bg-surface text-surface-body',
          )}
        >
          {ep.label ?? ep.id.slice(0, 8)}
        </button>
      ))}
      <span class="mx-1 h-4 w-px bg-line" />
      <label class="flex items-center gap-1 text-[12px] text-surface-muted">
        From
        <input
          type="date"
          value={dateFrom.value ?? ''}
          onInput={(e) => {
            const v = (e.currentTarget as HTMLInputElement).value;
            dateFrom.value = v.length === 0 ? null : v;
            page.value = 0;
          }}
          class="h-7 rounded border border-line bg-surface px-2 text-surface-fg"
        />
      </label>
      <label class="flex items-center gap-1 text-[12px] text-surface-muted">
        To
        <input
          type="date"
          value={dateTo.value ?? ''}
          onInput={(e) => {
            const v = (e.currentTarget as HTMLInputElement).value;
            dateTo.value = v.length === 0 ? null : v;
            page.value = 0;
          }}
          class="h-7 rounded border border-line bg-surface px-2 text-surface-fg"
        />
      </label>
      {anyFilter ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            resetFilters();
          }}
        >
          Reset
        </Button>
      ) : null}
    </div>
  );
}

/** Numbered pagination. Shows at most seven slots with an ellipsis. */
function Pagination() {
  const pages = pageCount();
  if (total.value === 0) return null;
  const current = page.value;
  const first = current * PAGE_SIZE + 1;
  const last = Math.min(total.value, (current + 1) * PAGE_SIZE);

  const shown = new Set([0, pages - 1, current, current - 1, current + 1]);
  const nums = [...shown]
    .filter((n) => n >= 0 && n < pages)
    .sort((a, b) => a - b);

  return (
    <div class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-line bg-surface-2 px-4 py-3">
      <span class="font-mono text-[11.5px] text-surface-muted tabular-nums">
        Showing {String(first)}–{String(last)} of {String(total.value)}
      </span>
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          aria-label="Previous page"
          disabled={current === 0}
          onClick={() => {
            setPage(current - 1);
          }}
          class="h-7 rounded border border-line bg-surface px-2 text-[13px] disabled:opacity-40"
        >
          ‹
        </button>
        {nums.map((n, i) => (
          <>
            {i > 0 && n - (nums[i - 1] ?? 0) > 1 ? (
              <span
                key={`gap-${String(n)}`}
                class="text-[13px] text-surface-muted"
              >
                …
              </span>
            ) : null}
            <button
              key={n}
              type="button"
              onClick={() => {
                setPage(n);
              }}
              class={cn(
                'h-7 min-w-7 rounded border px-2 text-[13px] tabular-nums',
                n === current
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'border-line bg-surface text-surface-body',
              )}
            >
              {String(n + 1)}
            </button>
          </>
        ))}
        <button
          type="button"
          aria-label="Next page"
          disabled={current >= pages - 1}
          onClick={() => {
            setPage(current + 1);
          }}
          class="h-7 rounded border border-line bg-surface px-2 text-[13px] disabled:opacity-40"
        >
          ›
        </button>
      </div>
    </div>
  );
}

export function AlertsRoute() {
  const [selecting, setSelecting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fingerprint = `${dateFrom.value ?? ''}|${dateTo.value ?? ''}|${endpointFilter.value.join(',')}|${String(showArchived.value)}|${String(page.value)}`;
  useEffect(() => {
    void refresh();
  }, [fingerprint]);

  async function withSweep(fn: () => Promise<unknown>): Promise<void> {
    setSweepBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSweepBusy(false);
    }
  }

  async function onArchive(row: AlertRow): Promise<void> {
    setError(null);
    try {
      await archive(row.id);
      setToast({ id: row.id, label: endpointLabel(row) });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const archived = showArchived.value;
  const rows = alerts.value;
  const selectedCount = selection.value.size;
  const groups = groupByDay(rows);

  return (
    <main class="mx-auto max-w-3xl px-6 py-8">
      {/* Header states the work, not a headline. */}
      <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div class="eyebrow mb-1.5">Alerts</div>
          <h1 class="text-[26px] font-semibold leading-tight tracking-[-0.015em]">
            {archived ? (
              <>{String(archivedTotal.value)} archived</>
            ) : (
              <>
                <span class="text-signal">{String(inboxTotal.value)}</span> in
                your inbox
              </>
            )}
          </h1>
          <p class="mt-0.5 text-[13px] text-surface-muted">
            {archived
              ? `Permanently deleted ${String(retentionDays.value)} days after archiving.`
              : 'Classify what matters, then archive it.'}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void refresh();
            }}
          >
            Refresh
          </Button>
          <Button
            variant={selecting ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setSelecting(!selecting);
              selection.value = new Set();
            }}
          >
            {selecting ? 'Done' : 'Select'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div class="mb-3 inline-flex overflow-hidden rounded-[4px] border border-line">
        <button
          type="button"
          onClick={() => {
            setTab(false);
          }}
          class={cn(
            'h-7 px-3 text-[13px]',
            archived
              ? 'bg-surface text-surface-body'
              : 'bg-surface-fg text-white font-medium',
          )}
        >
          Inbox {String(inboxTotal.value)}
        </button>
        <button
          type="button"
          onClick={() => {
            setTab(true);
          }}
          class={cn(
            'h-7 px-3 text-[13px]',
            archived
              ? 'bg-surface-fg text-white font-medium'
              : 'bg-surface text-surface-body',
          )}
        >
          Archive {String(archivedTotal.value)}
        </button>
      </div>

      <div class="mb-3">
        <FilterBar />
      </div>

      {/* Sweeps — at the top, where the pile is. */}
      <div class="mb-4 flex flex-wrap items-center gap-2 rounded-[4px] border border-line bg-surface-2 px-3.5 py-2.5">
        <span class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-fg">
          Sweep
        </span>
        <span class="mx-1 h-4 w-px bg-line" />
        {archived ? (
          <>
            <button
              type="button"
              disabled={sweepBusy || archivedTotal.value === 0}
              onClick={() =>
                void withSweep(() => restoreBulk({ scope: 'all' }))
              }
              class="flex h-6.5 items-center gap-1.5 rounded border border-line bg-surface px-2.5 py-1 text-[12.5px] text-surface-body disabled:opacity-40"
            >
              <ArchiveRestore size={13} aria-hidden />
              Restore all {String(archivedTotal.value)}
            </button>
            <button
              type="button"
              disabled={sweepBusy || archivedTotal.value === 0}
              onClick={() => {
                setConfirmPurge(true);
              }}
              class="flex h-6.5 items-center gap-1.5 rounded border border-signal bg-surface px-2.5 py-1 text-[12.5px] text-signal disabled:opacity-40"
            >
              <Trash2 size={13} aria-hidden />
              Purge archive now
            </button>
            <span class="ml-auto text-[12.5px] text-surface-muted">
              Purging is immediate and permanent.
            </span>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={sweepBusy}
              onClick={() =>
                void withSweep(() => archiveBulk({ scope: 'triaged' }))
              }
              class="flex h-6.5 items-center gap-1.5 rounded border border-line bg-surface px-2.5 py-1 text-[12.5px] text-surface-body disabled:opacity-40"
            >
              <Archive size={13} aria-hidden />
              Archive all triaged
            </button>
            <button
              type="button"
              disabled={sweepBusy}
              onClick={() =>
                void withSweep(() => archiveBulk({ scope: 'older_than_30d' }))
              }
              class="flex h-6.5 items-center gap-1.5 rounded border border-line bg-surface px-2.5 py-1 text-[12.5px] text-surface-body disabled:opacity-40"
            >
              <Archive size={13} aria-hidden />
              Archive older than 30 days
            </button>
            <span class="ml-auto text-[12.5px] text-surface-muted">
              Archiving never deletes.
            </span>
          </>
        )}
      </div>

      {error !== null ? (
        <p
          class="mb-3 rounded-[4px] border border-signal bg-signal-bg px-3 py-2 text-[13px] text-signal"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {loading.value && rows.length === 0 && (
        <p class="text-surface-muted">Loading…</p>
      )}

      {selecting && rows.length > 0 ? (
        <button
          type="button"
          onClick={() => {
            toggleSelectAll();
          }}
          class="mb-2 text-[13px] font-medium text-surface-fg"
        >
          Select all {String(rows.length)} on this page
        </button>
      ) : null}

      <div class="space-y-5">
        {groups.map((group) => (
          <section key={group.label}>
            <div class="mb-2 flex items-center gap-2.5 border-t border-line pt-2">
              <span class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-fg">
                {group.label}
              </span>
              <span class="h-px flex-1 bg-line" />
              <span class="font-mono text-[11px] text-surface-muted">
                {String(group.rows.length)}
              </span>
            </div>
            <div class="grid gap-2.5">
              {group.rows.map((a) =>
                !archived && a.triage_state === 'unreviewed' ? (
                  <AlertCard
                    key={a.id}
                    row={a}
                    selecting={selecting}
                    onArchive={(r) => void onArchive(r)}
                  />
                ) : (
                  <CompactRow
                    key={a.id}
                    row={a}
                    selecting={selecting}
                    onArchive={(r) => void onArchive(r)}
                  />
                ),
              )}
            </div>
          </section>
        ))}
        {!loading.value && rows.length === 0 && <EmptyStateSummary />}
      </div>

      <Pagination />

      {/* Undo toast — the whole reason archiving needs no confirmation. */}
      {toast !== null ? (
        <div class="fixed inset-x-4 bottom-4 z-40 mx-auto flex max-w-md items-center gap-3 rounded-[4px] bg-surface-fg px-4 py-3 text-white shadow-xl">
          <Archive size={15} aria-hidden />
          <span class="text-[13.5px]">
            Archived · deletes in {String(retentionDays.value)} days
          </span>
          <button
            type="button"
            onClick={() => {
              const id = toast.id;
              setToast(null);
              void restore(id)
                .then(() => refresh())
                .catch((e: unknown) => {
                  setError(e instanceof Error ? e.message : String(e));
                });
            }}
            class="ml-auto text-[13px] font-medium underline"
          >
            Undo
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              setToast(null);
            }}
            class="text-white/60"
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* Selection action bar. Delete never appears on the inbox tab. */}
      {selecting && selectedCount > 0 ? (
        <div class="fixed inset-x-4 bottom-4 z-30 mx-auto flex max-w-2xl flex-wrap items-center gap-3 rounded-[4px] bg-surface-fg px-4 py-3 text-white shadow-xl">
          <span class="text-[13.5px] font-semibold">
            {String(selectedCount)} selected
          </span>
          <span class="h-4 w-px bg-white/25" />
          {archived ? (
            <>
              <button
                type="button"
                onClick={() =>
                  void withSweep(() =>
                    restoreBulk({ ids: [...selection.value] }),
                  )
                }
                class="text-[13px] text-white/90"
              >
                Restore
              </button>
              <button
                type="button"
                onClick={() =>
                  void withSweep(() => purge({ ids: [...selection.value] }))
                }
                class="ml-auto flex items-center gap-1.5 rounded bg-signal px-3 py-1.5 text-[13px] font-semibold"
              >
                <Trash2 size={13} aria-hidden />
                Delete {String(selectedCount)} permanently
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() =>
                void withSweep(() => archiveBulk({ ids: [...selection.value] }))
              }
              class="ml-auto flex items-center gap-1.5 rounded bg-white px-3 py-1.5 text-[13px] font-semibold text-surface-fg"
            >
              <Archive size={13} aria-hidden />
              Archive {String(selectedCount)}
            </button>
          )}
        </div>
      ) : null}

      {/* The one dialog left: plural, irreversible, easy to mis-click. */}
      <ConfirmDialog
        open={confirmPurge}
        onOpenChange={setConfirmPurge}
        title="Purge archive"
        body={`This permanently deletes all ${String(archivedTotal.value)} archived alerts now, rather than waiting out the ${String(retentionDays.value)}-day window. Screenshots and detection data cannot be recovered.`}
        confirmLabel={`Delete ${String(archivedTotal.value)}`}
        destructive
        onConfirm={async () => {
          await purge({ scope: 'all' });
          await refresh();
        }}
      />
    </main>
  );
}
